import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolRadioGroupField } from '#src/components/validated-tool-form/shared/ValidatedToolRadioGroupField.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface MMSEFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const binaryChoice = z.enum(['correct', 'incorrect']).nullable()

const schema = z.object({
  'orientation-time-year': binaryChoice,
  'orientation-time-season': binaryChoice,
  'orientation-time-date': binaryChoice,
  'orientation-time-day': binaryChoice,
  'orientation-time-month': binaryChoice,
  'orientation-place-state': binaryChoice,
  'orientation-place-county': binaryChoice,
  'orientation-place-town': binaryChoice,
  'orientation-place-hospital': binaryChoice,
  'orientation-place-floor': binaryChoice,
  'registration-object-1': binaryChoice,
  'registration-object-2': binaryChoice,
  'registration-object-3': binaryChoice,
  'registration-trials': z.number().int().nullable(),
  'attention-serial-7s-1': binaryChoice,
  'attention-serial-7s-2': binaryChoice,
  'attention-serial-7s-3': binaryChoice,
  'attention-serial-7s-4': binaryChoice,
  'attention-serial-7s-5': binaryChoice,
  'recall-object-1': binaryChoice,
  'recall-object-2': binaryChoice,
  'recall-object-3': binaryChoice,
  'language-naming-pencil': binaryChoice,
  'language-naming-watch': binaryChoice,
  'language-repetition': binaryChoice,
  'language-command-1': binaryChoice,
  'language-command-2': binaryChoice,
  'language-command-3': binaryChoice,
  'language-read-obey': binaryChoice,
  'language-write-sentence': binaryChoice,
  'language-copy-design': binaryChoice,
  'level-of-consciousness': z
    .enum(['alert', 'drowsy', 'stupor', 'coma'])
    .nullable(),
  summary: z.string().nullable(),
})

const BINARY_OPTIONS = [
  { value: 'correct' as const, label: 'Correct' },
  { value: 'incorrect' as const, label: 'Incorrect' },
]

const validatedTool = new ValidatedTool(questionnaires.mmseQuestionnaire)

function BinaryItem({
  control,
  name,
  label,
  suggestion,
}: {
  control: UseFormReturn<any>['control']
  name: string
  label: string
  suggestion?: ValidatedToolFieldSuggestion
}): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <span className="w-48 shrink-0 text-sm font-medium">{label}</span>
      <ValidatedToolRadioGroupField
        control={control}
        name={name}
        options={BINARY_OPTIONS}
        className="flex gap-4"
        suggestion={suggestion}
      />
    </div>
  )
}

export function MMSEForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: MMSEFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="30-point cognitive screening tool"
      form={form}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}

      <ValidatedToolSection title="Orientation to Time" subtitle="5 points">
        <div className="grid gap-2">
          <BinaryItem
            control={form.control}
            name="orientation-time-year"
            suggestion={fieldSuggestions?.['orientation-time-year']}
            label="Year"
          />
          <BinaryItem
            control={form.control}
            name="orientation-time-season"
            suggestion={fieldSuggestions?.['orientation-time-season']}
            label="Season"
          />
          <BinaryItem
            control={form.control}
            name="orientation-time-date"
            suggestion={fieldSuggestions?.['orientation-time-date']}
            label="Date"
          />
          <BinaryItem
            control={form.control}
            name="orientation-time-day"
            suggestion={fieldSuggestions?.['orientation-time-day']}
            label="Day of week"
          />
          <BinaryItem
            control={form.control}
            name="orientation-time-month"
            suggestion={fieldSuggestions?.['orientation-time-month']}
            label="Month"
          />
        </div>
      </ValidatedToolSection>

      <ValidatedToolSection title="Orientation to Place" subtitle="5 points">
        <div className="grid gap-2">
          <BinaryItem
            control={form.control}
            name="orientation-place-state"
            suggestion={fieldSuggestions?.['orientation-place-state']}
            label="State"
          />
          <BinaryItem
            control={form.control}
            name="orientation-place-county"
            suggestion={fieldSuggestions?.['orientation-place-county']}
            label="County"
          />
          <BinaryItem
            control={form.control}
            name="orientation-place-town"
            suggestion={fieldSuggestions?.['orientation-place-town']}
            label="Town/City"
          />
          <BinaryItem
            control={form.control}
            name="orientation-place-hospital"
            suggestion={fieldSuggestions?.['orientation-place-hospital']}
            label="Hospital/Building"
          />
          <BinaryItem
            control={form.control}
            name="orientation-place-floor"
            suggestion={fieldSuggestions?.['orientation-place-floor']}
            label="Floor"
          />
        </div>
      </ValidatedToolSection>

      <ValidatedToolSection title="Registration" subtitle="3 points">
        <div className="grid gap-2">
          <BinaryItem
            control={form.control}
            name="registration-object-1"
            suggestion={fieldSuggestions?.['registration-object-1']}
            label="Object 1"
          />
          <BinaryItem
            control={form.control}
            name="registration-object-2"
            suggestion={fieldSuggestions?.['registration-object-2']}
            label="Object 2"
          />
          <BinaryItem
            control={form.control}
            name="registration-object-3"
            suggestion={fieldSuggestions?.['registration-object-3']}
            label="Object 3"
          />
          <FormField
            control={form.control}
            name="registration-trials"
            render={({ field: { value, onChange, ...rest } }) => (
              <div className="flex items-center gap-3">
                <span className="w-48 shrink-0 text-sm font-medium">
                  Number of trials
                </span>
                <Input
                  value={value ?? ''}
                  onChange={(e) =>
                    onChange(
                      e.target.value === '' ? null : e.target.valueAsNumber
                    )
                  }
                  type="number"
                  min={0}
                  className="w-20"
                  {...rest}
                />
              </div>
            )}
          />
        </div>
      </ValidatedToolSection>

      <ValidatedToolSection
        title="Attention & Calculation"
        subtitle="5 points — Serial 7s"
      >
        <div className="grid gap-2">
          <BinaryItem
            control={form.control}
            name="attention-serial-7s-1"
            suggestion={fieldSuggestions?.['attention-serial-7s-1']}
            label="1st subtraction (93)"
          />
          <BinaryItem
            control={form.control}
            name="attention-serial-7s-2"
            suggestion={fieldSuggestions?.['attention-serial-7s-2']}
            label="2nd subtraction (86)"
          />
          <BinaryItem
            control={form.control}
            name="attention-serial-7s-3"
            suggestion={fieldSuggestions?.['attention-serial-7s-3']}
            label="3rd subtraction (79)"
          />
          <BinaryItem
            control={form.control}
            name="attention-serial-7s-4"
            suggestion={fieldSuggestions?.['attention-serial-7s-4']}
            label="4th subtraction (72)"
          />
          <BinaryItem
            control={form.control}
            name="attention-serial-7s-5"
            suggestion={fieldSuggestions?.['attention-serial-7s-5']}
            label="5th subtraction (65)"
          />
        </div>
      </ValidatedToolSection>

      <ValidatedToolSection title="Recall" subtitle="3 points">
        <div className="grid gap-2">
          <BinaryItem
            control={form.control}
            name="recall-object-1"
            suggestion={fieldSuggestions?.['recall-object-1']}
            label="Object 1"
          />
          <BinaryItem
            control={form.control}
            name="recall-object-2"
            suggestion={fieldSuggestions?.['recall-object-2']}
            label="Object 2"
          />
          <BinaryItem
            control={form.control}
            name="recall-object-3"
            suggestion={fieldSuggestions?.['recall-object-3']}
            label="Object 3"
          />
        </div>
      </ValidatedToolSection>

      <ValidatedToolSection title="Language" subtitle="9 points">
        <div className="grid gap-2">
          <BinaryItem
            control={form.control}
            name="language-naming-pencil"
            suggestion={fieldSuggestions?.['language-naming-pencil']}
            label="Naming — Pencil"
          />
          <BinaryItem
            control={form.control}
            name="language-naming-watch"
            suggestion={fieldSuggestions?.['language-naming-watch']}
            label="Naming — Watch"
          />
          <BinaryItem
            control={form.control}
            name="language-repetition"
            suggestion={fieldSuggestions?.['language-repetition']}
            label='Repetition — "No ifs, ands, or buts"'
          />
          <BinaryItem
            control={form.control}
            name="language-command-1"
            suggestion={fieldSuggestions?.['language-command-1']}
            label="3-stage — Takes paper in right hand"
          />
          <BinaryItem
            control={form.control}
            name="language-command-2"
            suggestion={fieldSuggestions?.['language-command-2']}
            label="3-stage — Folds paper in half"
          />
          <BinaryItem
            control={form.control}
            name="language-command-3"
            suggestion={fieldSuggestions?.['language-command-3']}
            label="3-stage — Puts paper on floor"
          />
          <BinaryItem
            control={form.control}
            name="language-read-obey"
            suggestion={fieldSuggestions?.['language-read-obey']}
            label='Read and obey — "Close your eyes"'
          />
          <BinaryItem
            control={form.control}
            name="language-write-sentence"
            suggestion={fieldSuggestions?.['language-write-sentence']}
            label="Write a sentence"
          />
          <BinaryItem
            control={form.control}
            name="language-copy-design"
            suggestion={fieldSuggestions?.['language-copy-design']}
            label="Copy design (pentagons)"
          />
        </div>
      </ValidatedToolSection>

      <ValidatedToolSection
        title="Level of Consciousness"
        subtitle="Not scored"
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="level-of-consciousness"
          suggestion={fieldSuggestions?.['level-of-consciousness']}
          options={[
            { value: 'alert', label: 'Alert' },
            { value: 'drowsy', label: 'Drowsy' },
            { value: 'stupor', label: 'Stupor' },
            { value: 'coma', label: 'Coma' },
          ]}
        />
      </ValidatedToolSection>

      <Score form={form} validatedTool={validatedTool} response={response} />

      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  const { color, label } = match(result.score)
    .when(
      (s) => s >= 24,
      () => ({ color: 'green' as const, label: 'Normal' })
    )
    .when(
      (s) => s >= 18,
      () => ({ color: 'yellow' as const, label: 'Mild Impairment' })
    )
    .when(
      (s) => s >= 10,
      () => ({ color: 'orange' as const, label: 'Moderate Impairment' })
    )
    .when(
      (s) => s >= 0,
      () => ({ color: 'red' as const, label: 'Severe Impairment' })
    )
    .otherwise(() => {
      throw new Error()
    })
  return (
    <div className="flex items-center gap-2">
      <h2>
        Score: <b>{result.score}</b> / 30
      </h2>
      <Badge color={color} className="mt-px">
        {label}
      </Badge>
    </div>
  )
}
